<template>
  <v-app>
    <!-- Dynamic Top Navbar -->
    <Navbar
      :user="user"
      :nav-links="navLinks"
      @toggle-drawer="drawer = !drawer"
    />

    <!-- Mobile Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      fixed
      temporary
      class="glass-sidebar mobile-drawer"
    >
      <div class="pa-6 d-flex flex-column fill-height">
        <v-list nav class="flex-grow-1">
          <template v-for="(group, gIdx) in navLinks">
            <v-list-group
              v-if="group.items && group.items.length > 0"
              :key="'group-' + gIdx"
              :prepend-icon="group.icon"
              active-class="primary--text"
              class="mb-1"
            >
              <template v-slot:activator>
                <v-list-item-title class="text-uppercase font-weight-bold">{{
                  group.title
                }}</v-list-item-title>
              </template>

              <v-list-item
                v-for="(item, iIdx) in group.items"
                :key="'item-' + gIdx + '-' + iIdx"
                :to="item.to"
                exact
                class="pl-8 mb-1"
              >
                <v-list-item-icon
                  ><v-icon x-small color="primary">{{
                    item.icon
                  }}</v-icon></v-list-item-icon
                >
                <v-list-item-title class="text-uppercase">{{
                  item.title
                }}</v-list-item-title>
              </v-list-item>
            </v-list-group>

            <!-- `href` marks an external app (the Dashboard) rather than a Vue route - see
                 the matching branch in Navbar.vue. -->
            <v-list-item
              v-else
              :key="'link-' + gIdx"
              :to="group.href ? undefined : group.to"
              :href="group.href"
              :target="group.href ? '_blank' : undefined"
              :rel="group.href ? 'noopener noreferrer' : undefined"
              exact
              class="mb-2"
            >
              <v-list-item-icon
                ><v-icon color="primary">{{
                  group.icon
                }}</v-icon></v-list-item-icon
              >
              <v-list-item-title class="text-uppercase font-weight-bold">{{
                group.title
              }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>

        <div class="mobile-drawer__auth mt-4">
          <v-btn
            v-if="user.id == null || user.id == -1"
            depressed
            color="primary"
            class="black--text font-weight-bold mobile-drawer__auth-btn"
            rounded
            block
            :href="authUrl('/auth/steam')"
          >
            <v-icon left size="20">mdi-steam</v-icon>
            Sign in via Steam
          </v-btn>

          <template v-else>
            <v-btn
              :to="'/user/' + user.id"
              depressed
              color="secondary"
              class="white--text font-weight-bold mobile-drawer__auth-btn mb-2"
              rounded
              block
            >
              <v-icon left size="20">mdi-account-circle</v-icon>
              {{ user.name }}
            </v-btn>
            <v-btn
              :href="authUrl('/logout')"
              depressed
              color="grey darken-3"
              class="white--text font-weight-bold mobile-drawer__auth-btn"
              rounded
              block
            >
              <v-icon left size="20">mdi-logout-variant</v-icon>
              {{ $t("Navbar.Logout") }}
            </v-btn>
          </template>
        </div>
      </div>
    </v-navigation-drawer>

    <v-main class="flex-grow-1">
      <v-container fluid class="app-shell pa-8">
        <router-view :key="$route.path" />
      </v-container>
    </v-main>
    <Footer />
  </v-app>
</template>

<script>
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import brand, { loadBrand } from "@/utils/brand";
export default {
  name: "App",
  data() {
    return {
      drawer: false,
      apiUrl: process.env?.VUE_APP_G5V_API_URL || "/api",
      dashboardUrl:
        process.env?.VUE_APP_DASHBOARD_URL || "https://dashboard.glhfparty.dk",
      user: {
        admin: false,
        steam_id: "",
        id: -1,
        super_admin: false,
        name: "",
        small_image: "",
        medium_image: "",
        large_image: ""
      }
    };
  },
  components: {
    Navbar,
    Footer
  },
  computed: {
    navLinks() {
      const groups = [
        {
          title: "Overview",
          icon: "mdi-view-dashboard-outline",
          to: "/"
        },
        {
          title: "Matches",
          icon: "mdi-sword-cross",
          to: "/matches"
        },
        {
          title: this.$t("Navbar.Teams"),
          icon: "mdi-account-group",
          items: [
            {
              title: this.$t("Navbar.AllTeams"),
              to: "/teams",
              icon: "mdi-format-list-bulleted"
            },
            {
              title: "Teams Leaderboard",
              to: "/leaderboard/teams",
              icon: "mdi-account-group"
            },
            {
              title: this.$t("Navbar.PlayerLeader"),
              to: "/leaderboard",
              icon: "mdi-medal"
            }
          ]
        },
        {
          title: "Tournaments",
          icon: "mdi-trophy",
          to: "/seasons"
        }
      ];

      if (this.user.id != null && this.user.id != -1) {
        if (this.user.admin == 1 || this.user.super_admin == 1) {
          // Add Servers only for admins and above
          groups.push({
            title: "SERVERS",
            to: "/servers",
            icon: "mdi-server",
            items: []
          });
        }
        // Settings is SUPER ADMINS ONLY - deliberately stricter than Servers above. It leaves
        // G5V entirely for the Dashboard, which is the only place these settings live, and only
        // super admins have a Dashboard account that can actually manage anything (same rule as
        // Navbar's isDashboardLinked). `href` rather than `to`: this is an external app, not a
        // Vue route.
        if (this.user.super_admin == 1) {
          groups.push({
            title: "SETTINGS",
            href: `${this.dashboardUrl}/Settings?tab=match`,
            icon: "mdi-cog",
            items: []
          });
        }
      }
      return groups;
    },
    // Site-wide brand from the Dashboard (see utils/brand.js); also the page title via the watcher.
    brandingName() {
      return brand.name;
    },
    pageTitle() {
      const route = this.$route || {};
      const id = route.params?.id;
      const names = {
        Home: "Overview",
        Matches: "Matches",
        "My Matches": "My Matches",
        Teams: "Teams",
        "Create Team": "Create Team",
        "My Teams": "My Teams",
        Team: "Team",
        "New Match": "New Match",
        Match: id ? `Match #${id}` : "Match",
        User: "Player",
        "My User": "My Profile",
        Seasons: "Tournaments",
        "My Seasons": "My Tournaments",
        Season: "Tournament",
        "My Servers": "Servers",
        Server: "Server",
        Metrics: "Metrics",
        Leaderboard: "Player Leaderboard",
        SeasonPlayerBoard: "Player Leaderboard",
        TeamBoard: "Teams Leaderboard",
        SeasonTeamBoard: "Teams Leaderboard"
      };
      const page = names[route.name] || route.name;
      return page ? `${this.brandingName} - ${page}` : this.brandingName;
    }
  },
  methods: {
    authUrl(path) {
      const base = this.apiUrl || "/api";
      return `${base}${path}`;
    },
    // Site-wide design picked in Dashboard -> Settings -> General ("hud" or "classic").
    // G5V keeps its own look; only the navbar follows it, via html[data-glhf-design="hud"]
    // (styles in Navbar.vue). Remembered in localStorage so a reload doesn't flash the
    // other navbar while theme.json is still in flight. Anything but an explicit
    // "classic" means HUD, the default - same rule as the Dashboard's UiDesignService.
    setGlhfDesign(design) {
      const value = design === "classic" ? "classic" : "hud";
      document.documentElement.setAttribute("data-glhf-design", value);
      try {
        window.localStorage.setItem("glhf-design", value);
      } catch (e) {
        // storage blocked (private mode) - the attribute alone is enough for this page view
      }
    },
    // Best-effort sync with the Dashboard's Customization theme.
    //
    // Two separate mechanisms drive color in this app, and both need to be
    // hit or most of the UI won't visibly change:
    //  - Vuetify 2's theme (primary/secondary/surface/...) — only ~30 files
    //    actually use theme-driven props (color="primary", v-card surface).
    //  - CSS custom properties declared in this file's :root block
    //    (--neon-cyan, --glass-bg, --glass-navbar-bg, --glass-border) —
    //    Navbar.vue and most "accent"/card styling (hover states, active
    //    tab, glow effects, .glass-card/.glass-navbar backgrounds+borders)
    //    reference these directly and never touch Vuetify's theme object,
    //    so setting theme.primary/theme.surface alone (the first version
    //    of this) had no visible effect on the navbar or the data cards.
    //
    // Vuetify has no equivalent for border color either way (only the CSS
    // var above), and there's no single "normal text" or "header text" CSS
    // var G5V uses consistently, so those two stay unmapped — see
    // Dashboard's Settings.cshtml.cs / Program.cs
    // "/theme.json" comment.
    applyDashboardTheme() {
      let remembered = null;
      try {
        remembered = window.localStorage.getItem("glhf-design");
      } catch (e) {
        // storage blocked - fall back to the default until theme.json answers
      }
      this.setGlhfDesign(remembered);

      const base = this.dashboardUrl || "https://dashboard.glhfparty.dk";
      fetch(`${base}/theme.json`)
        .then((res) => (res.ok ? res.json() : null))
        .then((theme) => {
          if (!theme) return;

          this.setGlhfDesign(theme.design);

          const root = document.documentElement.style;
          if (theme.navbarHoverColor) {
            root.setProperty("--neon-cyan", theme.navbarHoverColor);
          }
          if (theme.boxBgColor) {
            root.setProperty("--glass-bg", theme.boxBgColor);
          }
          if (theme.navbarBgColor) {
            root.setProperty("--glass-navbar-bg", theme.navbarBgColor);
          }
          if (theme.borderColor) {
            root.setProperty("--glass-border", theme.borderColor);
          }

          if (this.$vuetify) {
            const themes = this.$vuetify.theme.themes;
            for (const variant of [themes.dark, themes.light]) {
              if (!variant) continue;
              if (theme.navbarHoverColor) {
                variant.primary = theme.navbarHoverColor;
                variant.secondary = theme.navbarHoverColor;
              }
              if (theme.boxBgColor) {
                variant.surface = theme.boxBgColor;
              }
            }
          }
        })
        .catch(() => {});
    }
  },
  watch: {
    // Tab title = brand + the page you are on ("GLHF Lan - Matches").
    pageTitle: {
      immediate: true,
      handler(newTitle) {
        document.title = newTitle;
      }
    }
  },
  async mounted() {
    loadBrand(this.dashboardUrl);
    this.applyDashboardTheme();
    this.user = await this.IsLoggedIn();
  }
};
</script>
<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&family=Orbitron:wght@400;700&family=Oxanium:wght@600;700;800&display=swap");

:root {
  --neon-cyan: #f46e00;
  --neon-purple: #7000ff;
  --cyber-pink: #ff0055;
  --glass-bg: rgba(36, 36, 42, 0.75);
  --glass-navbar-bg: rgba(36, 36, 42, 0.75);
  --glass-border: rgba(255, 255, 255, 0.2);
}

.v-application {
  font-family: "Inter", sans-serif !important;
  background-color: #070709 !important;

  .font-orbitron {
    font-family: "Orbitron", sans-serif !important;
  }

  .headline,
  .title {
    text-transform: uppercase;
    letter-spacing: 2px !important;
  }
}

.glass-sidebar {
  background: rgba(13, 13, 17, 0.95) !important;
  border-right: 1px solid var(--glass-border) !important;
  backdrop-filter: blur(20px);
}

.mobile-drawer__auth {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 16px;
}

.mobile-drawer__auth-btn {
  min-height: 44px;
}

.sidebar-active {
  background: rgba(0, 242, 255, 0.1) !important;
  border-right: 3px solid var(--neon-cyan);

  .v-list-item__title {
    color: var(--neon-cyan) !important;
    text-shadow: 0 0 10px rgba(0, 242, 255, 0.5);
  }
}

.letter-spacing-1 {
  letter-spacing: 1px !important;
}

.title-glow {
  text-shadow: 0 0 15px rgba(0, 242, 255, 0.6);
}

.neon-btn {
  box-shadow: 0 0 20px rgba(0, 242, 255, 0.3) !important;
  &:hover {
    box-shadow: 0 0 30px rgba(0, 242, 255, 0.5) !important;
    transform: scale(1.02);
  }
}

/* Glassmorphism Classes */
.glass-card {
  background: var(--glass-bg) !important;
  backdrop-filter: blur(12px) !important;
  -webkit-backdrop-filter: blur(12px) !important;
  border: 1px solid var(--glass-border) !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37) !important;
}

/* Global Table Styling: the arena look from the match page (rounded row
   boxes, short orange Rajdhani headers) on every v-data-table. Phone layout
   (.v-data-table--mobile, rows become stacked cards) keeps Vuetify's own
   stacking with just the colours. */
.v-application .v-data-table {
  background: transparent !important;
  box-shadow: none !important;

  > .v-data-table__wrapper {
    background: rgba(22, 22, 26, 0.4) !important;
    border-radius: 12px;
    padding: 0 6px;
  }

  thead th,
  thead th span {
    font-family: "Rajdhani", sans-serif !important;
    font-weight: 700 !important;
    font-size: 12px !important;
    letter-spacing: 1.5px !important;
    text-transform: uppercase !important;
    color: #ff7a00 !important;
    border: 0 !important;
    white-space: nowrap;
  }

  thead th .v-icon {
    color: #fff !important;
  }

  tbody > tr > td {
    color: #d4d5da;
    font-size: 13px !important;
  }

  .v-data-footer {
    border-top: 0 !important;
  }

  .v-chip.v-size--x-small,
  .v-chip.v-size--small {
    border-radius: 5px !important;
    font-weight: 600;
  }
}

.v-application .v-data-table:not(.v-data-table--mobile) {
  > .v-data-table__wrapper > table {
    border-collapse: separate !important;
    border-spacing: 0 5px !important;
  }

  tbody > tr > td {
    height: 38px !important;
    background: #131418 !important;
    border-top: 1px solid #24262c !important;
    border-bottom: 1px solid #24262c !important;
  }

  tbody > tr > td:first-child {
    border-left: 1px solid #24262c !important;
    border-radius: 8px 0 0 8px;
  }

  tbody > tr > td:last-child {
    border-right: 1px solid #24262c !important;
    border-radius: 0 8px 8px 0;
  }

  tbody > tr:hover > td {
    background: #181a1f !important;
  }

  /* expand, empty/loading and group-header rows stay flat */
  tbody > tr.v-data-table__expanded__content > td,
  tbody > tr.v-data-table__empty-wrapper > td,
  tbody > tr.v-row-group__header > td,
  tbody > tr.v-row-group__header > th {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  tbody > tr.v-data-table__expanded__content,
  tbody > tr.v-row-group__header {
    background: transparent !important;
    box-shadow: none !important;
  }
}

.v-application .v-data-table--mobile tbody > tr > td {
  border-bottom: 1px solid #24262c !important;
}

.hover-link {
  text-decoration: none !important;
  transition: all 0.2s ease;

  &:hover {
    filter: brightness(1.2);
    text-shadow: 0 0 8px currentColor;
    text-decoration: none !important;
  }
}

.neon-btn-small {
  box-shadow: 0 0 10px rgba(0, 242, 255, 0.2) !important;
  transition: all 0.3s ease !important;

  &:hover {
    box-shadow: 0 0 20px rgba(0, 242, 255, 0.5) !important;
    transform: translateY(-1px);
  }
}

/* ---- Every popup (v-dialog), arena design ----
   Panel like ArenaPanel (dark, thin border, orange top edge), white italic uppercase title,
   one footer layout: buttons grouped on the right, Cancel first, all the same height; stacked
   full width on phones. Buttons are styled by role from the Vuetify props the dialogs already
   use (filled primary = main action, text / outlined = secondary, error = destructive), so a
   new dialog gets the look without extra classes. */
.v-dialog > .v-card {
  position: relative;
  overflow: hidden;
  background: #0f1013 !important;
  backdrop-filter: none !important;
  border: 1px solid #30323a !important;
  border-radius: 14px !important;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.6) !important;
}

.v-dialog > .v-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 3px;
  background: linear-gradient(90deg, #ff7a00, rgba(255, 122, 0, 0.15) 70%, transparent);
  pointer-events: none;
}

.v-application .v-dialog .v-card__title,
.v-application .v-dialog .v-card__title > span,
.v-application .v-dialog .v-card__title .headline,
.v-application .v-dialog .v-card__title .font-orbitron {
  font-family: "Titillium Web", sans-serif !important;
  font-style: italic;
  font-weight: 900 !important;
  font-size: 24px !important;
  line-height: 1.2 !important;
  letter-spacing: 0.3px !important;
  text-transform: uppercase;
  color: #fff !important;
  text-shadow: none !important;
  word-break: normal;
}

.v-dialog .v-card__subtitle,
.v-dialog .v-card__text {
  color: rgba(255, 255, 255, 0.72) !important;
}

.v-dialog .v-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.v-dialog .v-card__actions > .spacer {
  display: none;
}

.v-application .v-dialog .v-card__actions .v-btn {
  height: 44px !important;
  min-width: 112px !important;
  margin: 0 !important;
  padding: 0 18px !important;
  border-radius: 7px !important;
  font-family: "Rajdhani", sans-serif !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  letter-spacing: 1.4px !important;
  text-transform: uppercase;
  box-shadow: none !important;
}

/* secondary: text / outlined buttons (Cancel, No, Close, ...) */
.v-application .v-dialog .v-card__actions .v-btn.v-btn--text,
.v-application .v-dialog .v-card__actions .v-btn.v-btn--outlined {
  border: 1.5px solid #30323a !important;
  background: rgba(0, 0, 0, 0.45) !important;
  color: #c9cbd1 !important;
}

/* main action: filled buttons */
.v-application .v-dialog .v-card__actions .v-btn:not(.v-btn--text):not(.v-btn--outlined) {
  border: 1.5px solid transparent !important;
  background: linear-gradient(180deg, #ff9422, #ff7300) !important;
  color: #111 !important;
}

/* destructive: error-coloured buttons, filled or not */
.v-application .v-dialog .v-card__actions .v-btn.error,
.v-application .v-dialog .v-card__actions .v-btn.error--text,
.v-application .v-dialog .v-card__actions .v-btn.red,
.v-application .v-dialog .v-card__actions .v-btn.red--text {
  border: 1.5px solid #7a2326 !important;
  background: rgba(60, 14, 16, 0.75) !important;
  color: #ff6b6e !important;
}

.v-application .v-dialog .v-card__actions .v-btn::before {
  opacity: 0 !important;
}

.v-application .v-dialog .v-card__actions .v-btn:hover {
  filter: brightness(1.15);
}

@media (max-width: 560px) {
  .v-dialog .v-card__title,
  .v-dialog .v-card__title > span,
  .v-dialog .v-card__title .headline {
    font-size: 20px !important;
  }
  .v-dialog .v-card__actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .v-application .v-dialog .v-card__actions .v-btn {
    width: 100%;
  }
}

.v-dialog__content--active::before {
  opacity: 0.18 !important;
}

/* Confirm popups, arena design (same family as ArenaPanel / arena.scss): dark panel with an
   orange top edge, Rajdhani labels, Titillium italic title, arena-style buttons. Shared by every
   confirm dialog in the app. */
.confirm-dialog-card {
  position: relative;
  overflow: hidden;
  background: #0f1013 !important;
  border: 1px solid #30323a !important;
  border-radius: 14px !important;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.6) !important;
}

.confirm-dialog-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 3px;
  background: linear-gradient(90deg, #ff7a00, rgba(255, 122, 0, 0.15) 70%, transparent);
}

.confirm-dialog-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.confirm-dialog-icon {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 122, 0, 0.08);
  border: 1.5px solid #ff7a00;
  flex-shrink: 0;
}

.confirm-dialog-icon .v-icon {
  color: #ff7a00 !important;
}

.confirm-dialog-kicker {
  font-family: "Rajdhani", sans-serif !important;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #ff7a00;
}

.confirm-dialog-title {
  font-family: "Titillium Web", sans-serif !important;
  font-style: italic;
  font-weight: 900;
  font-size: 26px;
  line-height: 1.15;
  letter-spacing: 0.3px !important;
  text-transform: uppercase !important;
  color: #fff;
}

.confirm-dialog-body {
  color: rgba(255, 255, 255, 0.72) !important;
  font-size: 0.98rem;
  line-height: 1.55;
}

.confirm-dialog-body b {
  color: #fff;
  word-break: break-all;
}

/* Footer: the way out (Cancel / No) on the left, the actions grouped on the right, every button
   the same height. Narrow screens stack them full width with the main action on top. */
.confirm-dialog-actions {
  display: flex !important;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 !important;
}



.confirm-dialog-actions > .v-btn {
  margin: 0 !important;
}

/* Team-name buttons (forfeit / advances): long names are cut with an ellipsis, not wrapped. */
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-team {
  max-width: 100%;
  text-transform: none;
}
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-team .v-btn__content {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-cancel,
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-confirm,
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-secondary,
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-danger {
  height: 44px !important;
  min-width: 112px;
  padding: 0 18px !important;
  border-radius: 7px !important;
  font-family: "Rajdhani", sans-serif !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  letter-spacing: 1.4px !important;
  text-transform: uppercase;
  box-shadow: none !important;
}

.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-cancel,
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-secondary {
  border: 1.5px solid #30323a !important;
  background: rgba(0, 0, 0, 0.45) !important;
  color: #c9cbd1 !important;
}

.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-secondary {
  border-color: #ff7a00 !important;
  color: #fff !important;
}

.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-confirm {
  border: 1.5px solid transparent !important;
  background: linear-gradient(180deg, #ff9422, #ff7300) !important;
  color: #111 !important;
  box-shadow: 0 0 10px rgba(255, 122, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
}

.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-danger {
  border: 1.5px solid #7a2326 !important;
  background: rgba(60, 14, 16, 0.75) !important;
  color: #ff6b6e !important;
}

.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-cancel:hover,
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-confirm:hover,
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-secondary:hover,
.v-application .v-dialog .v-card__actions .v-btn.confirm-dialog-danger:hover {
  filter: brightness(1.15);
}

.confirm-dialog-confirm::before,
.confirm-dialog-danger::before,
.confirm-dialog-secondary::before,
.confirm-dialog-cancel::before {
  opacity: 0 !important;
}

@media (max-width: 560px) {
  .confirm-dialog-title {
    font-size: 21px;
  }
  .confirm-dialog-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .confirm-dialog-actions > .v-btn,
  .confirm-dialog-actions .confirm-dialog-cancel {
    width: 100%;
    margin-right: 0 !important;
  }
  .confirm-dialog-actions > .spacer {
    display: none;
  }
}

.feedback-snackbar {
  bottom: 24px !important;
}

.feedback-snackbar .v-snack__wrapper {
  min-width: min(92vw, 460px) !important;
  max-width: min(92vw, 560px) !important;
  background: rgba(14, 14, 18, 0.94) !important;
  border: 1px solid rgba(255, 138, 0, 0.28) !important;
  border-radius: 16px !important;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.38) !important;
  backdrop-filter: blur(18px) !important;
  -webkit-backdrop-filter: blur(18px) !important;
}

.feedback-snackbar__content {
  padding: 0 !important;
}

.feedback-snackbar__inner {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
}

.feedback-snackbar__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 138, 0, 0.12);
  border: 1px solid rgba(255, 138, 0, 0.22);
  flex: 0 0 auto;
}

.feedback-snackbar__label {
  color: rgba(255, 138, 0, 0.92);
  font-family: "Orbitron", sans-serif !important;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 4px;
}

.feedback-snackbar__message {
  color: #f7f7fb;
  font-weight: 700;
  line-height: 1.45;
  word-break: break-word;
}

.feedback-snackbar__message--pre {
  white-space: pre-wrap;
  font-family: Consolas, monospace;
  font-size: 0.82rem;
}

.feedback-snackbar--success .v-snack__wrapper {
  border-color: rgba(87, 219, 124, 0.28) !important;
}

.feedback-snackbar--success .feedback-snackbar__icon {
  background: rgba(87, 219, 124, 0.12);
  border-color: rgba(87, 219, 124, 0.24);
}

.feedback-snackbar--success .feedback-snackbar__label {
  color: #57db7c;
}

.feedback-snackbar--error .v-snack__wrapper {
  border-color: rgba(255, 82, 82, 0.28) !important;
}

.feedback-snackbar--error .feedback-snackbar__icon {
  background: rgba(255, 82, 82, 0.12);
  border-color: rgba(255, 82, 82, 0.24);
}

.feedback-snackbar--error .feedback-snackbar__label {
  color: #ff6b6b;
}

/* Global Vuetify Overrides */
.v-text-field--filled > .v-input__control > .v-input__slot {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  &:hover {
    background: rgba(255, 255, 255, 0.08) !important;
  }
}

.v-input--is-focused > .v-input__control > .v-input__slot {
  border-color: var(--neon-cyan) !important;
  box-shadow: 0 0 10px rgba(0, 242, 255, 0.2) !important;
}

/* Force Checkbox & Radio Labels White */
/* Force Checkbox & Radio Labels White */

.v-select__selections {
  color: white !important;
}
.v-list {
  background: rgba(13, 13, 17, 0.95) !important;
}
.v-list-item__title {
  color: white !important;
}

/* Stepper Customization */
.v-stepper {
  background: transparent !important;
  box-shadow: none !important;
}
.v-stepper__header {
  box-shadow: none !important;
  border-bottom: 1px solid var(--glass-border);
}
.v-stepper__step--active .v-stepper__step__step {
  background-color: var(--neon-cyan) !important;
}

/* Pagination Overrides */
.v-pagination__item--active {
  background-color: var(--neon-cyan) !important;
  color: black !important;
}

.glass-navbar {
  background: var(--glass-bg) !important;
  backdrop-filter: blur(10px) !important;
  border-bottom: 1px solid var(--glass-border) !important;
}

/* Neon Scrollbar */
::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-track {
  background: #0a0a0c;
}
::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, var(--neon-purple), var(--neon-cyan));
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--neon-cyan);
}

.v-main {
  background-image: radial-gradient(
      circle at 50% 50%,
      rgba(112, 0, 255, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 0% 0%,
      rgba(0, 242, 255, 0.05) 0%,
      transparent 40%
    );
}

/* Date Picker Glass Theme Override */
.v-picker.v-card {
  background: rgba(13, 13, 17, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border: 1px solid var(--glass-border) !important;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5) !important;
}

/* Header section with year and currently selected date */
.v-picker__title {
  background: rgba(0, 242, 255, 0.05) !important;
  border-bottom: 1px solid var(--glass-border) !important;
}

/* Make internal table transparent so glassmorphism translates through */
.v-date-picker-table {
  background: transparent !important;
}

.v-date-picker-table table {
  background: transparent !important;
}

/* Neon ring on the active selected date */
.v-date-picker-table .v-btn.v-btn--active {
  color: #000000 !important;
  background-color: var(--neon-cyan) !important;
  box-shadow: 0 0 10px rgba(0, 242, 255, 0.5) !important;
}

/* Current day (today) styling */
.v-date-picker-table .v-btn--active.v-btn--text {
  border: 1px solid var(--neon-cyan) !important;
  color: var(--neon-cyan) !important;
  background-color: transparent !important;
}

/* Range highlighting */
.v-date-picker-table__events > div {
  background-color: var(--neon-pink) !important;
}

.app-shell {
  width: 100%;
}

@media (max-width: 600px) {
  .v-main {
    overflow-x: hidden;
  }

  .app-shell {
    padding: 12px !important;
  }

  .glass-navbar {
    height: 64px !important;
  }

  .glass-navbar .v-toolbar__title {
    font-size: 1rem !important;
    letter-spacing: 1px !important;
  }

  .glass-navbar .v-app-bar__nav-icon {
    width: 40px !important;
    height: 40px !important;
  }

  .glass-navbar .v-btn--icon {
    width: 40px !important;
    height: 40px !important;
  }

  .mobile-drawer {
    width: min(84vw, 290px) !important;
  }

  .mobile-drawer .pa-6 {
    padding: 14px !important;
  }

  .mobile-drawer .v-list {
    padding-top: 0 !important;
  }

  .mobile-drawer .v-list-item {
    min-height: 40px !important;
    padding-left: 10px !important;
    padding-right: 10px !important;
  }

  .mobile-drawer .v-list-item__icon {
    margin-right: 10px !important;
  }

  .mobile-drawer .v-list-item__title {
    font-size: 0.92rem !important;
    line-height: 1.2 !important;
  }

  .mobile-drawer .v-list-group__header {
    min-height: 40px !important;
  }
}
</style>
